/**
 * Zustand store for message management
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  ConversationStatus,
  ToolCallConfirmationDetails,
  ChatCompressionInfo
} from '../../../shared-types/src/index';
import { detectAndFireBillingPaywall } from '../services/billingPaywallDetector';

// Thinking history for tracking AI thought process
export interface ThinkingEntry {
  subject: string;
  timestamp: number;
  completed: boolean;
}

// Tool execution progress tracking
export interface ToolExecutionStep {
  id: string;
  toolName: string;
  status: 'pending' | 'executing' | 'completed' | 'error';
  startTime?: Date;
  endTime?: Date;
  duration?: number;
  description: string;
  parameters?: Record<string, any>;
  result?: string;
  error?: string;
}

export interface ToolExecutionProgress {
  messageId: string;
  steps: ToolExecutionStep[];
  currentStepId?: string;
  startTime: Date;
  estimatedTotalTime?: number;
}

export interface Message {
  id: string;
  conversationId: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  attachments: FileAttachment[];
  metadata: {
    modelName?: string;
    streamComplete: boolean;
    processingTime?: number;
    tokenCount?: number; // Legacy field for backward compatibility
    inputTokenCount?: number; // Total tokens sent to model (including context)
    outputTokenCount?: number; // Tokens in model response
    error?: any;
    toolExecutions?: CompletedToolExecution[]; // Persistent tool execution history
    toolCalls?: Array<{
      id: string;
      name: string;
      status: 'pending' | 'completed' | 'error';
      parameters?: any;
      result?: any;
      startedAt?: string;
      completedAt?: string;
    }>; // Active tool calls during streaming
    isCompressionBoundary?: boolean;
    compressionInfo?: ChatCompressionInfo;
    chatCompressed?: boolean; // Flag indicating chat was compressed
    includedInContext?: boolean;
    isSystemGenerated?: boolean;
    isPlaceholder?: boolean; // Flag to indicate this is a placeholder message to show immediate feedback
    lastChunkIndex?: number; // Track last processed chunk to prevent duplicates
    isThinkingContent?: boolean; // Flag indicating current stream contains thinking content that should be filtered
    restoredFromChunks?: boolean; // Flag to indicate this message was restored from Redis chunks
    restoredAt?: Date; // When the message was restored
    progress?: {
      percentage: number;
      chunks?: number;
      currentOperation?: string;
    }; // Job queue progress tracking
    recovered?: boolean; // Flag indicating message was recovered from interrupted job
    isStreaming?: boolean; // Flag indicating message is currently streaming
    jobId?: string; // Job ID for async job queue
    workingDirectory?: string; // Working directory for the conversation
    completedAt?: string; // When the message completed
    interrupted?: boolean; // Flag indicating message was stopped by user
    slidesUpdated?: boolean; // Flag indicating slides were updated during this message
    slideUpdateCount?: number; // Number of slides updated
    scriptsUpdated?: boolean; // Flag indicating narration scripts were updated during this message
    scriptUpdateSlideIds?: string[]; // Slide IDs whose scripts were updated
    retryStatus?: {
      isRetrying: boolean;
      attempt: number;
      maxAttempts: number;
      nextRetryIn: number;
      remainingSeconds?: number;
      totalSeconds?: number;
      error: string;
      delayMs: number;
    };
  };
  status: 'sending' | 'sent' | 'error' | 'streaming' | 'completed';
  // Hybrid thinking state - combines old and new approaches
  thinkingHistory: ThinkingEntry[]; // All thinking subjects shown so far
  currentThinkingSubject?: string; // Currently active thinking subject (during streaming)
  currentThinking?: string; // Full thinking content for chapter extraction
}

// Completed tool execution record for persistent storage
export interface CompletedToolExecution {
  id: string;
  toolName: string;
  status: 'completed' | 'error';
  startTime: Date;
  endTime: Date;
  duration: number;
  parameters: Record<string, any>;
  result?: string;
  error?: string;
  description: string;
  images?: Array<{
    data: string; // base64 encoded image data
    mimeType: string;
    filename?: string;
  }>;
}

export interface FileAttachment {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  url: string;
  type: 'image' | 'document';
  uploadedAt: Date;
}

interface MessageStore {
  // State
  messagesByConversation: Record<string, Message[]>;
  isLoading: boolean;
  error: string | null;
  streamingMessageId: string | null;
  conversationStatus: ConversationStatus;
  conversationStatusReason?: string;
  pendingConfirmations: Record<string, {
    toolCallId: string;
    details: ToolCallConfirmationDetails;
  }>;
  toolExecutionProgress: Record<string, ToolExecutionProgress>;
  compressionStatus: Record<string, {
    isCompressing: boolean;
    lastCompressionAt?: Date;
  }>;

  // Actions
  setMessages: (conversationId: string, messages: Message[]) => void;
  addMessage: (message: Message) => void;
  removeMessage: (messageId: string) => void;
  updateMessage: (messageId: string, updates: Partial<Message>) => void;
  appendToMessage: (messageId: string, content: string) => void;
  
  // Simplified thinking actions
  addThinkingSubject: (messageId: string, subject: string) => void;
  setCurrentThinking: (messageId: string, subject: string | undefined) => void;
  completeCurrentThinking: (messageId: string) => void;
  clearAllThinking: (messageId: string) => void;
  deleteMessage: (messageId: string) => void;
  clearMessages: (conversationId: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  setStreamingMessage: (messageId: string | null) => void;
  setConversationStatus: (status: ConversationStatus, reason?: string) => void;
  addPendingConfirmation: (messageId: string, toolCallId: string, details: ToolCallConfirmationDetails) => void;
  removePendingConfirmation: (toolCallId: string) => void;
  getPendingConfirmations: () => Array<{ messageId: string; toolCallId: string; details: ToolCallConfirmationDetails }>;
  
  // Tool execution progress actions
  startToolExecution: (messageId: string, toolName: string, parameters?: Record<string, any>) => string;
  updateToolStep: (messageId: string, stepId: string, updates: Partial<ToolExecutionStep>) => void;
  completeToolStep: (messageId: string, stepId: string, result?: string) => void;
  errorToolStep: (messageId: string, stepId: string, error: string) => void;
  clearToolProgress: (messageId: string) => void;
  getToolProgress: (messageId: string) => ToolExecutionProgress | null;
  
  // Persistent tool execution history actions
  saveToolExecutionToMessage: (messageId: string, toolExecution: CompletedToolExecution) => void;
  getToolExecutionsForMessage: (messageId: string) => CompletedToolExecution[];
  
  // Compression actions
  setCompressionStatus: (conversationId: string, isCompressing: boolean) => void;
  addCompressionMessage: (conversationId: string, compressionInfo: ChatCompressionInfo) => void;

  // Conversation switching actions
  clearConversationState: (conversationId: string) => void;

  // Stream restoration actions
  restoreMessageFromChunks: (messageId: string, chunks: any[], reconstructedContent: string) => void;
  getLastChunkIndex: (conversationId: string) => number;
  findIncompleteStreamingMessage: (conversationId: string) => Message | null;

  // Computed getters
  getMessagesForConversation: (conversationId: string) => Message[];
  getMessageById: (messageId: string) => Message | null;
  getLastUserMessage: (conversationId: string) => Message | null;
  isMessageStreaming: (messageId: string) => boolean;
  isConversationCompressing: (conversationId: string) => boolean;
}

// Helper to normalize message timestamp from API (string) to Date object
// API returns 'createdAt' but frontend expects 'timestamp'
const normalizeMessageTimestamp = (message: Message): Message => {
  // Get the timestamp from either 'timestamp' or 'createdAt' (API field name)
  const rawTimestamp = message.timestamp || (message as any).createdAt;
  const normalizedTimestamp = rawTimestamp instanceof Date
    ? rawTimestamp
    : new Date(rawTimestamp);

  return {
    ...message,
    timestamp: normalizedTimestamp,
  };
};

export const useMessageStore = create<MessageStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      messagesByConversation: {},
      isLoading: false,
      error: null,
      streamingMessageId: null,
      conversationStatus: ConversationStatus.Completed,
      conversationStatusReason: undefined,
      pendingConfirmations: {},
      toolExecutionProgress: {},
      compressionStatus: {},

      // Actions
      setMessages: (conversationId, messages) => {
        console.log(`📥 MessageStore: setMessages called for conversation ${conversationId}`);
        console.log(`  Loading ${messages.length} messages`);
        
        // Check for messages with inputTokenCount
        const messagesWithInputTokens = messages.filter(m => m.metadata?.inputTokenCount);
        if (messagesWithInputTokens.length > 0) {
          console.log(`  ✅ Found ${messagesWithInputTokens.length} messages with inputTokenCount:`);
          messagesWithInputTokens.forEach(m => {
            console.log(`    Message ${m.id}: inputTokenCount = ${m.metadata.inputTokenCount}`);
          });
        } else {
          console.log(`  ❌ No messages have inputTokenCount`);
        }
        
        // Normalize timestamps from API strings to Date objects
        const normalizedMessages = messages.map(normalizeMessageTimestamp);

        set(
          (state) => ({
            messagesByConversation: {
              ...state.messagesByConversation,
              [conversationId]: normalizedMessages,
            },
          }),
          false,
          'setMessages'
        );
      },

      addMessage: (message) => {
        // Normalize timestamp from API string to Date object
        const normalizedMessage = normalizeMessageTimestamp(message);
        set(
          (state) => {
            // Check for duplicates before adding
            const existingMessages = state.messagesByConversation[normalizedMessage.conversationId] || [];
            const isDuplicate = existingMessages.some(existingMsg => existingMsg.id === normalizedMessage.id);

            if (isDuplicate) {
              console.warn('⚠️ STORE DUPLICATE: Message already exists in store, skipping:', normalizedMessage.id);
              return state; // Return current state unchanged
            }

            console.debug('📝 STORE: Adding message to store:', normalizedMessage.id, normalizedMessage.role);
            detectAndFireBillingPaywall(normalizedMessage.content);
            return {
              messagesByConversation: {
                ...state.messagesByConversation,
                [normalizedMessage.conversationId]: [
                  ...existingMessages,
                  normalizedMessage,
                ],
              },
            };
          },
          false,
          'addMessage'
        );
      },

      removeMessage: (messageId) => {
        set(
          (state) => {
            console.debug('🗑️ STORE: Removing message from store:', messageId);
            const newMessagesByConversation = { ...state.messagesByConversation };
            
            // Find and remove the message from the appropriate conversation
            for (const conversationId in newMessagesByConversation) {
              const messages = newMessagesByConversation[conversationId];
              const filteredMessages = messages.filter((msg) => msg.id !== messageId);
              
              if (filteredMessages.length !== messages.length) {
                console.debug('🗑️ STORE: Message removed from conversation:', conversationId);
                newMessagesByConversation[conversationId] = filteredMessages;
                
                // Also clear streaming message ID if it matches
                if (state.streamingMessageId === messageId) {
                  return {
                    messagesByConversation: newMessagesByConversation,
                    streamingMessageId: null
                  };
                }
                break;
              }
            }
            
            return { messagesByConversation: newMessagesByConversation };
          },
          false,
          'removeMessage'
        );
      },

      updateMessage: (messageId, updates) => {
        console.log(`📝 MessageStore: updateMessage called for ${messageId}`);
        console.log(`  Updates:`, JSON.stringify(updates, null, 2));
        
        set(
          (state) => {
            const newMessagesByConversation = { ...state.messagesByConversation };
            
            // Find and update the message in the appropriate conversation
            for (const conversationId in newMessagesByConversation) {
              const messages = newMessagesByConversation[conversationId];
              const messageIndex = messages.findIndex((msg) => msg.id === messageId);
              
              if (messageIndex !== -1) {
                const oldMessage = messages[messageIndex];
                const newMessage = { ...oldMessage, ...updates };
                
                console.log(`  Found message in conversation ${conversationId}`);
                console.log(`  Old metadata:`, JSON.stringify(oldMessage.metadata, null, 2));
                console.log(`  New metadata:`, JSON.stringify(newMessage.metadata, null, 2));
                
                if (updates.metadata?.inputTokenCount) {
                  console.log(`  ✅ Setting inputTokenCount: ${updates.metadata.inputTokenCount}`);
                }
                
                newMessagesByConversation[conversationId] = [
                  ...messages.slice(0, messageIndex),
                  newMessage,
                  ...messages.slice(messageIndex + 1),
                ];
                break;
              } else if (conversationId === Object.keys(newMessagesByConversation)[Object.keys(newMessagesByConversation).length - 1]) {
                console.log(`  ❌ Message ${messageId} not found in any conversation`);
              }
            }
            
            return { messagesByConversation: newMessagesByConversation };
          },
          false,
          'updateMessage'
        );
      },

      appendToMessage: (messageId, content) => {
        detectAndFireBillingPaywall(content);
        set(
          (state) => {
            const newMessagesByConversation = { ...state.messagesByConversation };

            // Find and append to the message content
            for (const conversationId in newMessagesByConversation) {
              const messages = newMessagesByConversation[conversationId];
              const messageIndex = messages.findIndex((msg) => msg.id === messageId);
              
              if (messageIndex !== -1) {
                const message = messages[messageIndex];
                newMessagesByConversation[conversationId] = [
                  ...messages.slice(0, messageIndex),
                  { ...message, content: message.content + content },
                  ...messages.slice(messageIndex + 1),
                ];
                break;
              }
            }
            
            return { messagesByConversation: newMessagesByConversation };
          },
          false,
          'appendToMessage'
        );
      },

      addThinkingSubject: (messageId, subject) => {
        set(
          (state) => {
            const newMessagesByConversation = { ...state.messagesByConversation };
            
            for (const conversationId in newMessagesByConversation) {
              const messages = newMessagesByConversation[conversationId];
              const messageIndex = messages.findIndex((msg) => msg.id === messageId);
              
              if (messageIndex !== -1) {
                const message = messages[messageIndex];
                
                // Check if subject already exists to avoid duplicate updates
                const existingEntry = message.thinkingHistory?.find(t => t.subject === subject);
                if (existingEntry) {
                  // Subject already exists, no need to update
                  return state;
                }
                
                // Create new thinking history with the new entry
                const thinkingHistory = [
                  ...(message.thinkingHistory || []),
                  {
                    subject,
                    timestamp: Date.now(),
                    completed: false
                  }
                ];
                
                // Create updated message
                const updatedMessage = { 
                  ...message, 
                  thinkingHistory,
                  currentThinkingSubject: subject
                };
                
                // Update messages array efficiently
                const newMessages = [...messages];
                newMessages[messageIndex] = updatedMessage;
                newMessagesByConversation[conversationId] = newMessages;
                
                break;
              }
            }
            
            return { messagesByConversation: newMessagesByConversation };
          },
          false,
          'addThinkingSubject'
        );
      },

      setCurrentThinking: (messageId, subject) => {
        set(
          (state) => {
            const newMessagesByConversation = { ...state.messagesByConversation };
            
            for (const conversationId in newMessagesByConversation) {
              const messages = newMessagesByConversation[conversationId];
              const messageIndex = messages.findIndex((msg) => msg.id === messageId);
              
              if (messageIndex !== -1) {
                const message = messages[messageIndex];
                newMessagesByConversation[conversationId] = [
                  ...messages.slice(0, messageIndex),
                  { 
                    ...message, 
                    currentThinkingSubject: subject
                  },
                  ...messages.slice(messageIndex + 1),
                ];
                break;
              }
            }
            
            return { messagesByConversation: newMessagesByConversation };
          },
          false,
          'setCurrentThinking'
        );
      },

      completeCurrentThinking: (messageId) => {
        set(
          (state) => {
            const newMessagesByConversation = { ...state.messagesByConversation };
            
            for (const conversationId in newMessagesByConversation) {
              const messages = newMessagesByConversation[conversationId];
              const messageIndex = messages.findIndex((msg) => msg.id === messageId);
              
              if (messageIndex !== -1) {
                const message = messages[messageIndex];
                const thinkingHistory = [...(message.thinkingHistory || [])];
                
                // Mark current thinking as completed
                if (message.currentThinkingSubject) {
                  const entry = thinkingHistory.find(t => t.subject === message.currentThinkingSubject);
                  if (entry) {
                    entry.completed = true;
                  }
                }
                
                newMessagesByConversation[conversationId] = [
                  ...messages.slice(0, messageIndex),
                  { 
                    ...message, 
                    thinkingHistory,
                    currentThinkingSubject: undefined
                  },
                  ...messages.slice(messageIndex + 1),
                ];
                break;
              }
            }
            
            return { messagesByConversation: newMessagesByConversation };
          },
          false,
          'completeCurrentThinking'
        );
      },

      clearAllThinking: (messageId) => {
        set(
          (state) => {
            const newMessagesByConversation = { ...state.messagesByConversation };
            
            for (const conversationId in newMessagesByConversation) {
              const messages = newMessagesByConversation[conversationId];
              const messageIndex = messages.findIndex((msg) => msg.id === messageId);
              
              if (messageIndex !== -1) {
                const message = messages[messageIndex];
                newMessagesByConversation[conversationId] = [
                  ...messages.slice(0, messageIndex),
                  { 
                    ...message, 
                    thinkingHistory: [],
                    currentThinkingSubject: undefined,
                    currentThinking: undefined
                  },
                  ...messages.slice(messageIndex + 1),
                ];
                break;
              }
            }
            
            return { messagesByConversation: newMessagesByConversation };
          },
          false,
          'clearAllThinking'
        );
      },

      // Content segment methods - DEPRECATED, keeping for backward compatibility
      // TODO: Remove these once all references are updated
      addContentSegment: (messageId: string, type: string, content: string) => {
        // Use crypto.randomUUID for truly unique segment IDs
        const segmentId = `${messageId}-${type}-${Date.now()}-${crypto.randomUUID().substring(0, 8)}`;
        set(
          (state) => {
            const newMessagesByConversation = { ...state.messagesByConversation };
            
            // Find and update the message with new content segment
            for (const conversationId in newMessagesByConversation) {
              const messages = newMessagesByConversation[conversationId];
              const messageIndex = messages.findIndex((msg) => msg.id === messageId);
              
              if (messageIndex !== -1) {
                const message = messages[messageIndex];
                const newSegment: any = {
                  id: segmentId,
                  type,
                  content,
                  timestamp: new Date(),
                  isComplete: false
                };
                
                newMessagesByConversation[conversationId] = [
                  ...messages.slice(0, messageIndex),
                  { 
                    ...message, 
                    contentSegments: [...((message as any).contentSegments || []), newSegment]
                  } as any,
                  ...messages.slice(messageIndex + 1),
                ];
                break;
              }
            }
            
            return { messagesByConversation: newMessagesByConversation };
          },
          false,
          'addContentSegment'
        );
        return segmentId;
      },

      appendToContentSegment: (messageId: string, segmentId: string, content: string) => {
        set(
          (state) => {
            const newMessagesByConversation = { ...state.messagesByConversation };
            
            // Find and append to the content segment
            for (const conversationId in newMessagesByConversation) {
              const messages = newMessagesByConversation[conversationId];
              const messageIndex = messages.findIndex((msg) => msg.id === messageId);
              
              if (messageIndex !== -1) {
                const message = messages[messageIndex];
                const segments = (message as any).contentSegments || [];
                const segmentIndex = segments.findIndex((seg: any) => seg.id === segmentId);
                
                if (segmentIndex !== -1) {
                  const updatedSegments = [...segments];
                  updatedSegments[segmentIndex] = {
                    ...segments[segmentIndex],
                    content: segments[segmentIndex].content + content
                  };
                  
                  // Also update the legacy content field for backward compatibility
                  const responseSegments = updatedSegments.filter((seg: any) => seg.type === 'response');
                  const combinedContent = responseSegments.map((seg: any) => seg.content).join('');
                  
                  newMessagesByConversation[conversationId] = [
                    ...messages.slice(0, messageIndex),
                    { ...message, contentSegments: updatedSegments, content: combinedContent } as any,
                    ...messages.slice(messageIndex + 1),
                  ];
                }
                break;
              }
            }
            
            return { messagesByConversation: newMessagesByConversation };
          },
          false,
          'appendToContentSegment'
        );
      },

      completeContentSegment: (messageId: string, segmentId: string) => {
        set(
          (state) => {
            const newMessagesByConversation = { ...state.messagesByConversation };
            
            // Find and mark segment as complete
            for (const conversationId in newMessagesByConversation) {
              const messages = newMessagesByConversation[conversationId];
              const messageIndex = messages.findIndex((msg) => msg.id === messageId);
              
              if (messageIndex !== -1) {
                const message = messages[messageIndex];
                const segments = (message as any).contentSegments || [];
                const segmentIndex = segments.findIndex((seg: any) => seg.id === segmentId);
                
                if (segmentIndex !== -1) {
                  const updatedSegments = [...segments];
                  updatedSegments[segmentIndex] = {
                    ...segments[segmentIndex],
                    isComplete: true
                  };
                  
                  newMessagesByConversation[conversationId] = [
                    ...messages.slice(0, messageIndex),
                    { ...message, contentSegments: updatedSegments } as any,
                    ...messages.slice(messageIndex + 1),
                  ];
                }
                break;
              }
            }
            
            return { messagesByConversation: newMessagesByConversation };
          },
          false,
          'completeContentSegment'
        );
      },

      startNewResponseSegment: (messageId: string) => {
        // Complete any existing incomplete response segments
        const state = get();
        const message = state.getMessageById(messageId);
        if (message && (message as any).contentSegments) {
          const incompleteResponseSegment = (message as any).contentSegments.find(
            (seg: any) => seg.type === 'response' && !seg.isComplete
          );
          if (incompleteResponseSegment) {
            (state as any).completeContentSegment(messageId, incompleteResponseSegment.id);
          }
        }
        
        // Start a new response segment
        return (state as any).addContentSegment(messageId, 'response', '');
      },

      updateThinkingSegment: (messageId: string, thinking: string, subject?: string) => {
        const state = get();
        const message = state.getMessageById(messageId);
        
        if (message && (message as any).contentSegments) {
          // Find existing thinking segment or create new one
          let thinkingSegment = (message as any).contentSegments.find(
            (seg: any) => seg.type === 'thinking' && !seg.isComplete
          );
          
          if (thinkingSegment) {
            // Update existing thinking segment
            (state as any).appendToContentSegment(messageId, thinkingSegment.id, thinking);
          } else {
            // Create new thinking segment
            const segmentContent = subject ? `${subject}: ${thinking}` : thinking;
            (state as any).addContentSegment(messageId, 'thinking', segmentContent);
          }
        } else {
          // Fallback to creating new thinking segment
          const segmentContent = subject ? `${subject}: ${thinking}` : thinking;
          (state as any).addContentSegment(messageId, 'thinking', segmentContent);
        }
      },

      deleteMessage: (messageId) => {
        set(
          (state) => {
            const newMessagesByConversation = { ...state.messagesByConversation };
            
            // Find and remove the message
            for (const conversationId in newMessagesByConversation) {
              const messages = newMessagesByConversation[conversationId];
              newMessagesByConversation[conversationId] = messages.filter(
                (msg) => msg.id !== messageId
              );
            }
            
            return { messagesByConversation: newMessagesByConversation };
          },
          false,
          'deleteMessage'
        );
      },

      clearMessages: (conversationId) => {
        set(
          (state) => ({
            messagesByConversation: {
              ...state.messagesByConversation,
              [conversationId]: [],
            },
          }),
          false,
          'clearMessages'
        );
      },

      setLoading: (isLoading) => {
        set({ isLoading }, false, 'setLoading');
      },

      setError: (error) => {
        set({ error }, false, 'setError');
      },

      clearError: () => {
        set({ error: null }, false, 'clearError');
      },

      setStreamingMessage: (messageId) => {
        set({ streamingMessageId: messageId }, false, 'setStreamingMessage');
      },

      setConversationStatus: (status, reason) => {
        set({ 
          conversationStatus: status, 
          conversationStatusReason: reason 
        }, false, 'setConversationStatus');
      },

      addPendingConfirmation: (messageId, toolCallId, details) => {
        set(
          (state) => ({
            pendingConfirmations: {
              ...state.pendingConfirmations,
              [messageId]: { toolCallId, details }
            }
          }),
          false,
          'addPendingConfirmation'
        );
      },

      removePendingConfirmation: (toolCallId) => {
        set(
          (state) => {
            const newPendingConfirmations = { ...state.pendingConfirmations };
            for (const messageId in newPendingConfirmations) {
              if (newPendingConfirmations[messageId].toolCallId === toolCallId) {
                delete newPendingConfirmations[messageId];
                break;
              }
            }
            return { pendingConfirmations: newPendingConfirmations };
          },
          false,
          'removePendingConfirmation'
        );
      },

      getPendingConfirmations: () => {
        const { pendingConfirmations } = get();
        return Object.entries(pendingConfirmations).map(([messageId, confirmation]) => ({
          messageId,
          ...confirmation
        }));
      },

      // Computed getters
      getMessagesForConversation: (conversationId) => {
        const { messagesByConversation } = get();
        return messagesByConversation[conversationId] || [];
      },

      getMessageById: (messageId) => {
        const { messagesByConversation } = get();
        
        for (const conversationId in messagesByConversation) {
          const message = messagesByConversation[conversationId].find(
            (msg) => msg.id === messageId
          );
          if (message) return message;
        }
        
        return null;
      },
      
      getLastUserMessage: (conversationId) => {
        const { messagesByConversation } = get();
        const messages = messagesByConversation[conversationId] || [];
        
        // Find the last user message
        for (let i = messages.length - 1; i >= 0; i--) {
          if (messages[i].role === 'user') {
            return messages[i];
          }
        }
        
        return null;
      },

      isMessageStreaming: (messageId) => {
        const { streamingMessageId } = get();
        return streamingMessageId === messageId;
      },

      // Tool execution progress actions
      startToolExecution: (messageId, toolName, parameters) => {
        const stepId = `${toolName}-${Date.now()}`;
        const step: ToolExecutionStep = {
          id: stepId,
          toolName,
          status: 'executing',
          startTime: new Date(),
          description: `Executing ${toolName}`,
          parameters
        };

        set(
          (state) => {
            const existing = state.toolExecutionProgress[messageId];
            if (existing) {
              // Add to existing progress
              return {
                toolExecutionProgress: {
                  ...state.toolExecutionProgress,
                  [messageId]: {
                    ...existing,
                    steps: [...existing.steps, step],
                    currentStepId: stepId
                  }
                }
              };
            } else {
              // Create new progress
              return {
                toolExecutionProgress: {
                  ...state.toolExecutionProgress,
                  [messageId]: {
                    messageId,
                    steps: [step],
                    currentStepId: stepId,
                    startTime: new Date()
                  }
                }
              };
            }
          },
          false,
          'startToolExecution'
        );

        return stepId;
      },

      updateToolStep: (messageId, stepId, updates) => {
        set(
          (state) => {
            const progress = state.toolExecutionProgress[messageId];
            if (!progress) return state;

            const stepIndex = progress.steps.findIndex(step => step.id === stepId);
            if (stepIndex === -1) return state;

            const updatedSteps = [...progress.steps];
            updatedSteps[stepIndex] = { ...updatedSteps[stepIndex], ...updates };

            return {
              toolExecutionProgress: {
                ...state.toolExecutionProgress,
                [messageId]: {
                  ...progress,
                  steps: updatedSteps
                }
              }
            };
          },
          false,
          'updateToolStep'
        );
      },

      completeToolStep: (messageId, stepId, result) => {
        set(
          (state) => {
            const progress = state.toolExecutionProgress[messageId];
            if (!progress) return state;

            const stepIndex = progress.steps.findIndex(step => step.id === stepId);
            if (stepIndex === -1) return state;

            const step = progress.steps[stepIndex];
            const endTime = new Date();
            const duration = step.startTime ? (endTime.getTime() - step.startTime.getTime()) / 1000 : 0;

            const updatedSteps = [...progress.steps];
            updatedSteps[stepIndex] = {
              ...step,
              status: 'completed',
              endTime,
              duration,
              result
            };

            return {
              toolExecutionProgress: {
                ...state.toolExecutionProgress,
                [messageId]: {
                  ...progress,
                  steps: updatedSteps,
                  currentStepId: undefined
                }
              }
            };
          },
          false,
          'completeToolStep'
        );
      },

      errorToolStep: (messageId, stepId, error) => {
        set(
          (state) => {
            const progress = state.toolExecutionProgress[messageId];
            if (!progress) return state;

            const stepIndex = progress.steps.findIndex(step => step.id === stepId);
            if (stepIndex === -1) return state;

            const step = progress.steps[stepIndex];
            const endTime = new Date();
            const duration = step.startTime ? (endTime.getTime() - step.startTime.getTime()) / 1000 : 0;

            const updatedSteps = [...progress.steps];
            updatedSteps[stepIndex] = {
              ...step,
              status: 'error',
              endTime,
              duration,
              error
            };

            return {
              toolExecutionProgress: {
                ...state.toolExecutionProgress,
                [messageId]: {
                  ...progress,
                  steps: updatedSteps,
                  currentStepId: undefined
                }
              }
            };
          },
          false,
          'errorToolStep'
        );
      },

      clearToolProgress: (messageId) => {
        set(
          (state) => {
            const newProgress = { ...state.toolExecutionProgress };
            delete newProgress[messageId];
            return { toolExecutionProgress: newProgress };
          },
          false,
          'clearToolProgress'
        );
      },

      getToolProgress: (messageId) => {
        const { toolExecutionProgress } = get();
        return toolExecutionProgress[messageId] || null;
      },

      // Persistent tool execution history actions
      saveToolExecutionToMessage: (messageId, toolExecution) => {
        set(
          (state) => {
            const newMessagesByConversation = { ...state.messagesByConversation };
            
            // Find and update the message
            for (const conversationId in newMessagesByConversation) {
              const messages = newMessagesByConversation[conversationId];
              const messageIndex = messages.findIndex((msg) => msg.id === messageId);
              
              if (messageIndex !== -1) {
                const message = messages[messageIndex];
                const existingExecutions = message.metadata?.toolExecutions || [];

                newMessagesByConversation[conversationId] = [
                  ...messages.slice(0, messageIndex),
                  {
                    ...message,
                    metadata: {
                      ...message.metadata,
                      toolExecutions: [...existingExecutions, toolExecution]
                    }
                  },
                  ...messages.slice(messageIndex + 1),
                ];
                break;
              }
            }
            
            return { messagesByConversation: newMessagesByConversation };
          },
          false,
          'saveToolExecutionToMessage'
        );
      },

      getToolExecutionsForMessage: (messageId) => {
        const message = get().getMessageById(messageId);
        return message?.metadata?.toolExecutions || [];
      },

      // Compression actions
      setCompressionStatus: (conversationId, isCompressing) => {
        set(
          (state) => ({
            compressionStatus: {
              ...state.compressionStatus,
              [conversationId]: {
                ...state.compressionStatus[conversationId],
                isCompressing,
                lastCompressionAt: isCompressing ? undefined : new Date()
              }
            }
          }),
          false,
          'setCompressionStatus'
        );
      },

      addCompressionMessage: (conversationId, compressionInfo) => {
        const compressionMessageId = `compression-${Date.now()}`;
        const compressionMessage: Message = {
          id: compressionMessageId,
          conversationId,
          role: 'assistant', // System message type
          content: `Chat history compressed from ${compressionInfo.originalTokenCount.toLocaleString()} to ${compressionInfo.newTokenCount.toLocaleString()} tokens.`,
          timestamp: new Date(),
          attachments: [],
          metadata: {
            streamComplete: true,
            isCompressionBoundary: true,
            compressionInfo,
            includedInContext: true,
            isSystemGenerated: true
          },
          status: 'sent',
          thinkingHistory: [],
          currentThinkingSubject: undefined,
          currentThinking: undefined
        };

        set(
          (state) => ({
            messagesByConversation: {
              ...state.messagesByConversation,
              [conversationId]: [
                ...(state.messagesByConversation[conversationId] || []),
                compressionMessage
              ]
            },
            compressionStatus: {
              ...state.compressionStatus,
              [conversationId]: {
                isCompressing: false,
                lastCompressionAt: new Date()
              }
            }
          }),
          false,
          'addCompressionMessage'
        );
      },

      isConversationCompressing: (conversationId) => {
        const { compressionStatus } = get();
        return compressionStatus[conversationId]?.isCompressing || false;
      },

      // Clear global streaming state when switching away from a conversation
      clearConversationState: (newConversationId) => {
        set(
          (state) => {
            const currentStreamingMessage = state.getMessageById(state.streamingMessageId || '');
            
            // Clear global streaming state if we're switching to a different conversation
            // and the current streaming message belongs to a different conversation
            const shouldClearGlobalState = state.streamingMessageId && 
              currentStreamingMessage && 
              currentStreamingMessage.conversationId !== newConversationId;

            return {
              // Clear global streaming state if switching away from streaming conversation
              streamingMessageId: shouldClearGlobalState ? null : state.streamingMessageId,
              conversationStatus: shouldClearGlobalState ? ConversationStatus.Completed : state.conversationStatus,
              conversationStatusReason: shouldClearGlobalState ? undefined : state.conversationStatusReason,
              
              // Reset error state when switching conversations
              error: null
            };
          },
          false,
          'clearConversationState'
        );
      },

      // Restore message content from Redis chunks
      restoreMessageFromChunks: (messageId, chunks, reconstructedContent) => {
        set(
          (state) => {
            const newMessagesByConversation = { ...state.messagesByConversation };
            
            // Find and update the message
            for (const conversationId in newMessagesByConversation) {
              const messages = newMessagesByConversation[conversationId];
              const messageIndex = messages.findIndex((msg) => msg.id === messageId);
              
              if (messageIndex !== -1) {
                const message = messages[messageIndex];
                const lastChunkIndex = chunks.length > 0 ? Math.max(...chunks.map(c => c.index)) : -1;
                
                newMessagesByConversation[conversationId] = [
                  ...messages.slice(0, messageIndex),
                  {
                    ...message,
                    content: reconstructedContent,
                    metadata: {
                      ...message.metadata,
                      lastChunkIndex,
                      restoredFromChunks: true,
                      restoredAt: new Date()
                    }
                  },
                  ...messages.slice(messageIndex + 1),
                ];
                break;
              }
            }
            
            return { messagesByConversation: newMessagesByConversation };
          },
          false,
          'restoreMessageFromChunks'
        );
      },

      // Get the highest chunk index processed for a conversation
      getLastChunkIndex: (conversationId) => {
        const { messagesByConversation } = get();
        const messages = messagesByConversation[conversationId] || [];
        
        let maxChunkIndex = -1;
        messages.forEach(message => {
          if (message.metadata?.lastChunkIndex !== undefined) {
            maxChunkIndex = Math.max(maxChunkIndex, message.metadata.lastChunkIndex);
          }
        });
        
        return maxChunkIndex;
      },

      // Find incomplete streaming message in a conversation
      findIncompleteStreamingMessage: (conversationId) => {
        const { messagesByConversation } = get();
        const messages = messagesByConversation[conversationId] || [];

        // Find assistant message that's currently streaming or incomplete
        // Note: metadata can be null for some messages, so we need to handle that
        return messages.find(message =>
          message.role === 'assistant' &&
          (message.status === 'streaming' || (message.metadata && !message.metadata.streamComplete))
        ) || null;
      },
    }),
    {
      name: 'message-store',
    }
  )
);